import { AfterViewInit, Component, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ListItemRendererComponent, PimTemplateProperties } from 'gung-list';
import { first, forkJoin, of, Subject, Subscription } from 'rxjs';
import { Availability } from '../../models/availability';
import { CustomerProductPrice } from '../../models/price';
import { Product, ProductMultiDimension } from '../../models/product';
import { AuthService } from '../../services/auth/auth.service';
import { GungAnonymousConfigService } from '../../services/gung-anonymous-config/gung-anonymous-config.service';
import { GungFlowService } from '../../services/gung-flow/gung-flow.service';
import { PriceService } from '../../services/price/price.service';
import { GungFlow } from '../../state/flow/types';
import { isMultiDimensionProduct } from '../../utils/product-utils';


export interface GridViewProductCard {
  product: Product;
  price: CustomerProductPrice;
  availability: Availability;
}

@Component({
    selector: 'lib-product-grid-view',
    templateUrl: './product-grid-view.component.html',
    styleUrls: ['./product-grid-view.component.css'],
    standalone: false
})
export class ProductGridViewComponent
  extends ListItemRendererComponent<Product[]>
  implements OnInit, AfterViewInit, OnDestroy, OnChanges {

  isStandardErp = false;
  protected keyedMapData: { [id: string]: GridViewProductCard } = {};
  protected subscriptions: Subscription[] = [];
  protected flowSubscriptions: Subscription;

  public includeAvailability = false;
  public isSales = false;
  public isAdmin = false;
  public isAnonymous = true;
  public mappedData: GridViewProductCard[] = [];
  public itemsPerRow = 3;
  public isLoading = true;

  protected mappedDataChanged: Subject<void> = new Subject<void>();
  protected unsubscribe: Subject<void> = new Subject();

  public currentFlow: GungFlow;
  protected oldFlow: GungFlow;

  errorMessage = 'ERROR: ';
  findError = false;

  constructor(
    protected priceService: PriceService,
    protected gungFlowService: GungFlowService,
    protected authService: AuthService,
    protected gungAnonymousConfigService: GungAnonymousConfigService,
    protected router: Router
  ) {
    super();
  }

  public ngOnInit() {
    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales =
          roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
        this.isAdmin = roles.filter(role => role.toUpperCase() === 'ADMIN').length > 0;
        this.isAnonymous = roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
      });

    this.subscribeToProducts();
    this.flowSubscriptions = this.gungFlowService.getSelectedFlow().subscribe(flow => (this.currentFlow = flow));
  }

  ngAfterViewInit(): void {
    this.renderFinished?.emit();
  }

  public ngOnChanges() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.subscribeToProducts();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    this.flowSubscriptions?.unsubscribe();

    this.mappedDataChanged.complete();

    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected subscribeToProducts() {
    this.isLoading = true;
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }
    const newIds = this.data.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }

    const subscription = forkJoin([
      of(this.data),
      this.priceService.getCurrentCustomerPrices(newIds).pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ])
      .pipe(first())
      .subscribe(([productData, prices, flow]) => {
        let avs = [];
        if (this.isAnonymous) {
          this.includeAvailability = this.gungAnonymousConfigService.showAnonymousAvailabilities && (flow.useAvailabilities || flow.requireAvailability);
        } else {
          this.includeAvailability = flow.useAvailabilities || flow.requireAvailability;
        }
        if (this.includeAvailability) {
          avs = newIds.map(id => this.getProductAvailability(this.data.find(product => product.id === id)));
        }

        this.keyedMapData = {
          ...this.keyedMapData,
          ...newIds.reduce((acc, curr) => {
            const product = productData.filter(p => p && p.id === curr)[0];
            
            if (!product) {
              this.findError = true;
              this.errorMessage += 'No product found. ';
              throw new Error('No product found');
            }
            if (product?.extra?.gme) {
              this.isStandardErp = true;
            }

            const price = prices.filter(p => p && p.productId === curr)[0];
            if (!price) {
              this.findError = true;
              this.errorMessage += 'No price found.';
              throw new Error('No price found');
            }

            const av = avs.filter(p => p && p.productId === curr)[0];
            if (!av && this.includeAvailability) {
              this.findError = true;
              this.errorMessage += 'No availability found. ';
              throw new Error('No availability found');
            }
            const item: GridViewProductCard = this.mapItem(curr, product, price, av);
            return {
              ...acc,
              [curr]: item
            };
          }, {})
        };
        this.readMappedDataFromCache();
      });
    this.subscriptions.push(subscription);
  }

  goToDetail(product: Product): void {
    const productId = this.getProductIdToNavigateToDetail(product);

    if (!!product.extra?._routerUrl) {
      this.router.navigate([product.extra._routerUrl]);
    } else if (product?.productType === 'concept') {
      this.router.navigate(['/concept/', productId]);
    } else {
      this.router.navigate(['/product/', productId]);
    }
  }

  getProductIdToNavigateToDetail(product: Product): string {
    // in case product is multidimention
    // then to navigate to details, it will be always to navigate to a primary dimension product and in product details is also shown other colors
    // this is needed beacause the product grid view can be built:
    //    1. by product colors (the primary dimension only contains one element, and is shown in details the correct one)
    //    2. by modelID (primary dimension can contain several element, and by defautl is shown in details the first one)
    if (isMultiDimensionProduct(product)) {
      const productMultiDimension = product as ProductMultiDimension;
      return productMultiDimension.modelId + productMultiDimension.primaryDimension[0].id;
    }

    return product.id;
  }

  protected getProductAvailability(product: Product): Availability {
    if (product.extra.availabilities) {
      const availabilities = Object.keys(product.extra.availabilities).map(key => product.extra.availabilities[key]);
      if (availabilities.length > 0) {
        return availabilities[0];
      }
    }

    return null;
  }

  protected readMappedDataFromCache() {
    const ids = this.data.map(d => d.id);
    this.mappedData = ids.map(id => this.keyedMapData[id]);

    this.mappedDataChanged.next();
    this.isLoading = false;
  }

  protected mapItem(
    id: string,
    product: Product,
    price: CustomerProductPrice,
    availability: Availability
  ): GridViewProductCard {
    return {
      product,
      price,
      availability
    };
  }

  getDisplayName(headerKey: PimTemplateProperties){
    let displayName = !!headerKey.displayName && headerKey.displayName != "" ? headerKey.displayName : headerKey.translationKey;

    return displayName;
  }
}
